/**
 * Shared vocabulary for the M3 marketplace onboarding funnel engine.
 *
 * See workbench p199-m3-marketplace-onboarding-funnel:
 * - 106-m3-funnel-tech-spec.md ("Frontend", D-T1/D-T2)
 * - 105-m3-funnel-product-spec.md ("Users & postures")
 * - 108-m3-funnel-test-spec.md (M3.5 scenarios)
 *
 * The funnel engine is intentionally decoupled from the legacy
 * `OnboardingData` shape (lib/onboarding-types.ts) — the M3 capture fields
 * (income_shape, occupancy, rail, etc.) are new backend surface landing in a
 * parallel work stream. `FunnelAnswers` below is the funnel's OWN local
 * answer model; M3.6/M3.7 section implementations read/write it and are
 * responsible for mapping it onto the real `POST /api/onboarding` payload
 * once that contract lands.
 */

import type { ReactNode } from "react";

import type { OnboardingResponse } from "../../lib/api-types";
import type { UsePreviewPassportResult } from "./use-preview-passport";

/**
 * Posture resolution outcome. `legacy` means "do not mount the funnel at
 * all" — OnboardingGuard keeps the existing AnonOnboardingPage/OnboardingPage
 * split in that case. `marketplace` and `invited` both mount the funnel
 * engine, just with different step lists (see posture-configs.ts).
 */
export type Posture = "marketplace" | "invited" | "legacy";

/** Verification rail chosen at the rail-choice section. Unset until then. */
export type Rail = "bank_link" | "self_report";

/** Density mode drives grouping only — never field set, order, or events. */
export type Density = "desktop" | "mobile";

/**
 * Six-value income_shape enum (tech spec §"Question vocabulary"). Mirrors
 * the backend `model.IncomeShape` enum under construction in the parallel
 * backend work stream — kept as a local literal union here so the FE funnel
 * ships without a hard dependency on that package landing first.
 */
export type IncomeShape =
  | "salaried_w2"
  | "hourly_commission_w2"
  | "self_employed"
  | "asset_based"
  | "not_employed"
  | "other";

/**
 * Occupancy deal-fact (story 1/5). No longer asked as its own "who's it
 * for" question — the goal screen derives it (`GOAL_TO_OCCUPANCY` in
 * `sections/IntentSection.helpers.ts`) because the two questions duplicated
 * each other. The field survives: it gates `investor_addon` and rides the
 * readiness target via `POST /api/onboarding`.
 */
export type Occupancy = "primary" | "second_home" | "investment";

/**
 * Goal-screen selection (the funnel's first question). `second_home` was
 * folded in when the separate occupancy question was dropped — each goal
 * implies its occupancy.
 */
export type FunnelGoal =
  "first_home" | "moving_up" | "second_home" | "investment";

/** Intent-section timeline chip. */
export type FunnelTimeline = "asap" | "0_3_months" | "3_6_months" | "exploring";

/**
 * Shape-detail business-type presets (task:shape-detail-sections). `other`
 * is the escape hatch, mirrors the `income_shape` "other" precedent — never
 * dead, config-driven preset list per tech spec q180 (open, does not block
 * build).
 */
export type FunnelBusinessType =
  "consulting" | "trades" | "ecommerce" | "creative" | "other";

/**
 * Not-employed refinement outcome (tech spec "Not-working refinement").
 * Distinct from `incomeShape` because "living on savings / retired" flips
 * `incomeShape` to `asset_based` — this field remembers WHICH refinement
 * produced that transition (and gates whether the fork itself still needs
 * to render, vs. showing the resolved confirmation).
 */
export type NotEmployedRefinement = "retired" | "between_jobs" | "other_reason";

/** Investor add-on properties-owned bucket (task:investor-addon). */
export type InvestorPropertiesBucket =
  "first" | "one" | "two_to_four" | "five_plus";

/**
 * Credit-band chip used on the M3.7 rail screens (self-report financials +
 * fill-the-gaps). Adopts the canonical `credit_score` buckets from the
 * legacy wizard (`onboarding-types.ts` `CREDIT_SCORE_RANGES`) — `excellent
 * (740+)`, `good (700–739)`, `fair (660–699)`, `below (<660)` — rather than
 * the old coarse 3-band (`<680 / 680–740 / 740+`) that skipped `fair` and
 * put the bottom-bucket line at the wrong <680 boundary. The value IS the
 * backend `credit_score` frontend-enum value, so `rail-vocabulary.ts`
 * `creditBandToScoreEnum` is an identity pass-through kept only for
 * call-site stability. The bottom bucket (`below`, <660) is what the
 * marketplace admission evaluator treats as the low-credit steer
 * (`BottomCreditBandHigh=659`); `fair` (660–699) is "ready".
 */
export type FunnelCreditBand = "excellent" | "good" | "fair" | "below";

/**
 * Down-payment-source deal fact (tech spec §"Data model" —
 * `down_payment_source`; design chips "My own savings / A gift / Selling
 * investments / A mix"). Rides the readiness target's `Deal.DownPaymentSource`
 * (D-T6) once the deal is seeded — never a profile column.
 */
export type DownPaymentSource =
  "own_funds" | "gift" | "asset_derived" | "mixed";
export type EmploymentTenure = "under_1_year" | "1_to_2_years" | "2_plus_years";

/**
 * Outcome of a `POST /api/financials/derive/income` call (task:derive-
 * result-moment). `ok | insufficient_data | unavailable` mirror the real
 * backend statuses one-to-one (`backend/internal/api/financials_derive.go`
 * — `unavailable` fires when `PLAID_TRANSACTIONS_ENABLED=false`
 * server-side, a non-error outcome distinct from a fetch failure).
 * `revoked` is the FE-side classification of a Plaid ITEM_LOGIN_REQUIRED /
 * revoked-item failure (tech spec §"Error handling" — a distinct recovery
 * path from a generic `failed`); see `derive-income.ts` for why this
 * branch is currently unreachable against the real endpoint (a confirmed
 * backend gap, not a FE bug) despite being fully implemented and tested
 * here.
 */
export type DeriveOutcomeStatus =
  "ok" | "insufficient_data" | "failed" | "revoked" | "unavailable";

export interface DeriveIncomeOutcome {
  status: DeriveOutcomeStatus;
  /** Pre-formatted bucketed band string (e.g. "$4,000–$5,000/mo") — the
   * backend never sends the raw derived figure (D-T7 extended to the wire
   * boundary, `financialderive.MonthlyIncomeBand`). Render verbatim. */
  monthlyIncomeBand?: string | null;
  method?: string | null;
  asOf?: string | null;
}

/** Manual contributor rows created by this onboarding session. */
export type FinancialEntrySlot =
  | "income_manual"
  | "savings_manual"
  | "debts_manual"
  | "income_top_up"
  | "savings_top_up"
  | "debts_top_up";

/**
 * The funnel's own in-progress answer set. Grows as M3.6/M3.7 land real
 * section bodies; only the fields the M3.5 engine/resume logic needs to
 * reason about (conditional sections, rail dispatch, resume, strength) are
 * modeled here.
 *
 * The M3.6 fields below are additive and optional (never re-add a required
 * field to this interface — `resume.ts`/session-storage round-trips a plain
 * object literal and M3.5's own tests construct `FunnelAnswers` literals by
 * hand). They exist so search-intent/how-you-earn/shape-detail/investor-addon
 * answers survive the pre-account sessionStorage resume (product spec story
 * 11 — "nothing re-asked"), same as the M3.5 fields above.
 */
export interface FunnelAnswers {
  /** SEC-14: sharing level the buyer chose for the referring agent (account
   * step, only shown when an agent referral is present). Unset = untouched,
   * which submits as status_only. */
  agentSharingLevel?: "status_only" | "full_financial" | null;
  incomeShape: IncomeShape | null;
  occupancy: Occupancy | null;
  rail: Rail | null;
  /** True once the buyer has typed/derived an income figure (any rail). */
  hasIncomeSignal: boolean;
  /** True once savings/down-payment earmark has been captured. */
  hasSavingsSignal: boolean;
  /** True once the buyer has reported a credit band. */
  hasCreditSignal: boolean;

  // ── Intent section (task:search-intent-section) ──
  goal?: FunnelGoal | null;
  targetArea?: string | null;
  /** place_id of the picked area suggestion; cleared when the text is edited. */
  targetPlaceId?: string | null;
  targetPriceLow?: number | null;
  targetPriceHigh?: number | null;
  timeline?: FunnelTimeline | null;

  // ── Shape detail (task:shape-detail-sections) ──
  businessType?: FunnelBusinessType | null;
  ownershipPct?: number | null;
  selfEmployedIncomeEstimate?: number | null;
  assetCash?: number | null;
  assetBrokerage?: number | null;
  assetRetirement?: number | null;
  notEmployedRefinement?: NotEmployedRefinement | null;
  /** Optional capture; null means the server applies and discloses its default. */
  employmentTenure?: EmploymentTenure | null;
  employmentMonths?: number | null;

  // ── Investor add-on (task:investor-addon) ──
  investorPropertiesBucket?: InvestorPropertiesBucket | null;

  // ── Account step (task:account-step-wiring) ──
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /** Optional phone — captured on sign-up, persisted via /api/onboarding. */
  phone?: string | null;

  // ── Self-report financials (task:self-report-financials), reused by
  // fill-the-gaps (task:fill-the-gaps) for the bank-link rail's residual
  // fields the bank can't know ──
  /** Annual household income, dollars. Self-report rail only — the
   * bank-link rail derives/verifies income instead of asking. */
  householdIncome?: number | null;
  /** Total savings & liquid assets, dollars. Self-report rail only. */
  savings?: number | null;
  /** Monthly debts, dollars/mo. Self-report rail figure; on the bank-link
   * rail `monthlyDebtsTopUp` carries the fill-the-gaps top-up instead. */
  monthlyDebts?: number | null;
  creditBand?: FunnelCreditBand | null;
  /** Down-payment earmark, dollars. Capped at `savings` on the self-report
   * screen (scenario: "self-report financials include DP earmark"). */
  downPaymentEarmarked?: number | null;
  downPaymentSource?: DownPaymentSource | null;
  /** Fill-the-gaps confirm-a-bank-number chip results — `null` until
   * answered, `true` = "looks right" (bank figure stands, no top-up),
   * `false` = "add more" (the matching top-up amount below applies).
   * income/savings/debts each get the same confirm affordance so the buyer
   * can add a source the bank connection couldn't see (side income, an
   * account at another institution, a private loan). */
  incomeLookRight?: boolean | null;
  /** Additional ANNUAL income the bank connection couldn't see, dollars
   * (fill-the-gaps top-up, bank-link rail only). */
  annualIncomeTopUp?: number | null;
  savingsLookRight?: boolean | null;
  /** Additional savings / liquid assets held elsewhere, dollars
   * (fill-the-gaps top-up, bank-link rail only). */
  savingsTopUp?: number | null;
  debtsLookRight?: boolean | null;
  /** Additional monthly debt the bank connection couldn't see
   * (fill-the-gaps top-up, bank-link rail only). */
  monthlyDebtsTopUp?: number | null;
  /** Save each successful create before the remaining writes finish, so a
   * retry or same-tab refresh updates that row instead of adding it twice. */
  financialEntryIds?: Partial<Record<FinancialEntrySlot, string>>;

  // ── Verify / derive (task:verify-passport-skeleton /
  // task:derive-result-moment) ──
  /** True once the buyer has skipped the (always-skippable) verify step. */
  verifySkipped?: boolean;
  /** True once the simulated/real bank connection has completed. */
  bankConnected?: boolean;
  /** Local item UUID needed to finish bank sync after authenticator setup. */
  linkedBankItemId?: string;
  incomeDerived?: DeriveIncomeOutcome | null;

  // ── Preview / Go Live (task:steer-banner / task:go-live-states) ──
  /** Admission verdict from `GET /api/marketplace/passport`, cached here so
   * the merged `PassportGoLiveSection` can render "Go live anyway" + fire the
   * published-anyway analytics event even before/while the preview fetch
   * resolves. `null` until that fetch resolves — treated as "ready" (never
   * gates Go Live; D-T8). */
  admissionLevel?: "ready" | "improvable" | null;
  /** Fact-based reason codes backing an "improvable" admission level.
   * Never a numeric score (test spec "admission reasons never carry the
   * readiness score"). */
  admissionReasons?: string[] | null;
}

export function getEmptyFunnelAnswers(): FunnelAnswers {
  return {
    incomeShape: null,
    occupancy: null,
    rail: null,
    hasIncomeSignal: false,
    hasSavingsSignal: false,
    hasCreditSignal: false,
    goal: null,
    targetArea: null,
    targetPlaceId: null,
    targetPriceLow: null,
    targetPriceHigh: null,
    timeline: null,
    businessType: null,
    ownershipPct: null,
    selfEmployedIncomeEstimate: null,
    assetCash: null,
    assetBrokerage: null,
    assetRetirement: null,
    notEmployedRefinement: null,
    employmentTenure: null,
    employmentMonths: null,
    investorPropertiesBucket: null,
    email: null,
    firstName: null,
    householdIncome: null,
    savings: null,
    monthlyDebts: null,
    creditBand: null,
    downPaymentEarmarked: null,
    downPaymentSource: null,
    incomeLookRight: null,
    annualIncomeTopUp: null,
    savingsLookRight: null,
    savingsTopUp: null,
    debtsLookRight: null,
    monthlyDebtsTopUp: null,
    verifySkipped: false,
    bankConnected: false,
    incomeDerived: null,
    admissionLevel: null,
    admissionReasons: null,
  };
}

/**
 * Phases group sections for desktop rendering (funnel-step-engine density
 * scenario). Order here IS the canonical phase order; sections.ts sorts by
 * it.
 */
export type FunnelPhase = "landing" | "capture" | "rail" | "outcome";

export const FUNNEL_PHASE_ORDER: FunnelPhase[] = [
  "landing",
  "capture",
  "rail",
  "outcome",
];

/**
 * Every section the engine knows about. Rail-specific ids only ever appear
 * in a posture's resolved step list once a rail is chosen (see
 * posture-configs.ts `resolveSectionIds`).
 *
 * `landing` is a RESERVED id with no registry entry — no posture renders it
 * today (see sections.ts for why the placeholder splash was removed). It is
 * kept so the surfaces that already classify it (macro-step mapping, strength
 * chip) stay correct if a real in-app hero is ever commissioned, and so that
 * re-adding one is a single `ALL_SECTIONS` entry.
 */
export const FUNNEL_SECTION_IDS = [
  "landing",
  "intent",
  "intent_area",
  "intent_price",
  "intent_timeline",
  "how_you_earn",
  "shape_detail",
  "investor_addon",
  "rail_choice",
  "bank_account",
  "bank_link",
  "fill_the_gaps",
  "self_report_financials",
  "self_report_account",
  "verify_offer",
  "preview",
  "go_to_app",
] as const;
export type SectionId = (typeof FUNNEL_SECTION_IDS)[number];

/**
 * A section's structural definition — sections-as-data. `postures` gates
 * whether the section is part of a posture's step list AT ALL (structural,
 * e.g. `preview` never appears for `invited`); `isApplicable` gates whether
 * it appears given the CURRENT answers (conditional, e.g. `investor_addon`
 * only when occupancy is investment) — both must pass.
 *
 * The engine only needs this metadata to compute step lists, resume targets,
 * and grouping — it never hardcodes a component, so swapping a slot's
 * renderer requires no change to the engine, config, or resume logic.
 */
export interface FunnelSectionDef {
  id: SectionId;
  phase: FunnelPhase;
  postures: Posture[];
  rail?: Rail;
  /** Human label used by the placeholder + resume/test assertions. */
  label: string;
  isApplicable: (answers: FunnelAnswers) => boolean;
  /**
   * Explicit desktop-grouping opt-in (density.ts `groupSectionsForDensity`).
   * Sharing a `phase` is necessary but NOT sufficient for two consecutive
   * sections to land on the same desktop screen — the preceding AND the
   * following section must both set `groupable: true`. Defaults to
   * `undefined` (falsy = never groups), the SAFE default: a section whose
   * own Continue fires a network submit (Clerk signup, `/api/onboarding`,
   * a bank connect/derive call, a fill-the-gaps persist) must never end up
   * sharing a screen with a sibling whose Continue button could advance
   * past it without that submit running (the "desktop density silently
   * skips side-effectful section submits" defect). Only genuinely
   * side-effect-free, purely-local-state capture sections (intent,
   * how_you_earn, shape_detail, investor_addon) — see sections.ts.
   */
  groupable?: boolean;
  /**
   * The section's renderer. Every registry entry now points at a real screen
   * (the generic placeholder renderer is gone — see sections.ts) — the engine
   * always reads this off the data, never hardcodes a component, so swapping
   * a slot's renderer never touches FunnelEngine/posture-configs/resume.
   */
  Component: (props: FunnelSectionProps) => ReactNode;
}

export interface FunnelSectionProps {
  section: FunnelSectionDef;
  answers: FunnelAnswers;
  posture: Posture;
  density: Density;
  /**
   * Whether the marketplace is live (`useMarketplaceState() === "post"`),
   * resolved once by `FunnelEngine`. Gates every marketplace promise in the
   * organic funnel's copy ("until you go live", agents competing, incentive
   * figures) — `marketplace_funnel_enabled` alone only decides who gets the
   * funnel, not whether agents exist to compete.
   *
   * Optional, and absent means NOT live: a section rendered without it (a
   * test, a preview harness, a future caller that forgot to pass it) falls to
   * the pre-launch copy, which promises less. Only the launched marketplace
   * has to opt in.
   */
  marketplaceLive?: boolean;
  onAnswer: (patch: Partial<FunnelAnswers>) => void;
  onAdvance: () => void;
  /** Hold in-funnel navigation while verification or recovery-code setup is active. */
  onNavigationLockChange?: (locked: boolean) => void;
  /**
   * The `POST /api/onboarding` response from the account step, held in engine
   * state so the terminal screen can render the readiness outcome the submit
   * already returned. Deliberately NOT part of `FunnelAnswers`: that type is
   * the buyer's own answers (and is persisted to sessionStorage pre-account),
   * whereas this is server truth for the current run only.
   *
   * `null` until the account step completes. The terminal screen must treat
   * null as "no outcome to show" rather than assuming a score exists — a
   * resumed post-account run mounts the terminal without re-submitting.
   *
   * Optional because exactly one section produces it (the account step) and
   * one consumes it (the terminal screen); every other section ignores it,
   * and requiring it would only force 12 unrelated section tests to restate
   * a value they never use.
   */
  submitResult?: OnboardingResponse | null;
  /** Reports the account step's submit response up to engine state. */
  onSubmitResult?: (result: OnboardingResponse) => void;
  /**
   * The server's refusal of the buyer's area (`target_unresolvable` 422),
   * already rendered as a sentence. Engine-owned and shown by the area step:
   * the account step has no picker, so the refusal must be fixed where the
   * area is chosen. Cleared when the area text changes.
   */
  areaRefusal?: string | null;
  /**
   * Account step -> engine: `POST /api/onboarding` refused the area. The
   * engine returns the buyer to the area step with `message`.
   */
  onAreaRefused?: (message: string) => void;
  /**
   * True when the account step is re-entered after the buyer fixed a refused
   * area. The account (and its session) already exists, so the step must
   * resubmit with that session and never run Clerk sign-up again.
   */
  resubmitAfterAreaFix?: boolean;
  /**
   * The hoisted `usePreviewPassport` read, owned by `FunnelEngine` and shared
   * by every screen that renders the `PassportCard`. Engine-owned rather than
   * per-section because the engine is the only component mounted continuously
   * from the account step through the terminal, so the fetch it starts is
   * already resolving by the time an outcome screen paints.
   *
   * Optional for the same reason `submitResult` is: only the outcome screens
   * read it, and requiring it would force every unrelated section test to
   * restate a value it ignores. Absent is the honest "no passport read has
   * been made" state — treat it as such (pending card), never as an empty
   * passport.
   */
  previewPassport?: UsePreviewPassportResult;
}
